import Phaser from "phaser";
import { agritechQuestions } from "../agritechQuestions";
import { agritechQuestionsES } from "../agritechQuestionsES";
import { getAgritechText, getAgritechLanguage } from "../utils/agritechText";

export default class MainScene extends Phaser.Scene {
  constructor() {
    super("MainScene");
  }

  preload() {
    this.reportMainLoadProgress = (progress) => {
      window.dispatchEvent(
        new CustomEvent("biorev-game-loading-progress", {
          detail: { progress },
        }),
      );
    };

    this.finishMainLoadProgress = () => {
      this.reportMainLoadProgress(1);
      this.load.off("progress", this.reportMainLoadProgress);
    };

    this.load.on("progress", this.reportMainLoadProgress);
    this.load.once("complete", this.finishMainLoadProgress);

    this.events.once("shutdown", () => {
      this.load.off("progress", this.reportMainLoadProgress);
      this.load.off("complete", this.finishMainLoadProgress);
    });

    // NEW MOVEMENT SPRITES
    this.load.image("walk0", "/assets/movement/walk0.png");
    this.load.image("walk1", "/assets/movement/walk1.png");
    this.load.image("walk2", "/assets/movement/walk2.png");
    this.load.image("walk3", "/assets/movement/walk3.png");
    this.load.image("walk4", "/assets/movement/walk4.png");
    this.load.image("walk5", "/assets/movement/walk5.png");
    this.load.image("walk6", "/assets/movement/walk6.png");
    this.load.image("walk7", "/assets/movement/walk7.png");

    this.load.image("run1", "/assets/movement/run1.png");
    this.load.image("run2", "/assets/movement/run2.png");
    this.load.image("run3", "/assets/movement/run3.png");
    this.load.image("run4", "/assets/movement/run4.png");
    this.load.image("run5", "/assets/movement/run5.png");

    this.load.image("dive1", "/assets/movement/dive1.png");
    this.load.image("dive2", "/assets/movement/dive2.png");
    this.load.image("dive3", "/assets/movement/dive3.png");
    this.load.image("dive4", "/assets/movement/dive4.png");
    this.load.image("dive5", "/assets/movement/dive5.png");

    this.load.image("jump1", "/assets/movement/jump1.png");
    this.load.image("jump2", "/assets/movement/jump2.png");

    this.load.image("crouchFinal", "/assets/movement/crouchFinal.png");
    this.load.image("up", "/assets/movement/up.png");

    this.load.image("jumpup", "/assets/movement/jumpup.png");
    this.load.image("pound1", "/assets/movement/pound1.png");

    this.load.audio("correct", "/assets/correct.mp3");
    this.load.audio("incorrect", "/assets/incorrect.mp3");

    this.load.image("spring1", "/assets/Spring1.png");
    this.load.image("spring2", "/assets/Spring2.png");
    this.load.image("spring3", "/assets/Spring3.png");
    this.load.audio("running", "/assets/running.mp3");
    this.load.audio("runningLeaves", "/assets/runningLeaves.mp3");

    this.load.image("mud", "/assets/mud.png");
    this.load.image("mud2", "/assets/mud2.png");
    this.load.image("mudSplash", "/assets/mudSplash.png");

    this.load.image("bg", "/assets/background.png");
    this.load.image("titleBG", "/assets/titleBG.png");

    this.load.image("star", "/assets/star.png");
    this.load.image("greendrop", "/assets/greendrop.png");
    this.load.image("bluedrop", "/assets/bluedrop.png");
    this.load.image("brownDrop", "/assets/brownDrop.png");

    this.load.image("vacEffect", "/assets/vacEffect.png");

    this.load.audio("levelMusic", "/assets/level1.mp3");
    this.load.audio("springSound", "/assets/spring.mp3");
    this.load.audio("blueSound", "/assets/bluesound.wav");
    this.load.audio("greenSound", "/assets/greensound.wav");
    this.load.audio("powerGrab", "/assets/powerGrab.wav");
    this.load.audio("apply", "/assets/apply.wav");
    this.load.audio("vacuumLoop", "/assets/effect1.wav");
    this.load.audio("mudSplash1", "/assets/mudSplash1.wav");
    this.load.audio("mudSplash2", "/assets/mudSplash2.wav");
    this.load.audio("thunderclap", "/assets/thunderclap.mp3");
    this.load.audio("bounceClip", "/assets/BouncePlayer.mp3");
  }

  create() {
    const rightPanel = document.getElementById("right-ui-panel");
    if (rightPanel) rightPanel.style.visibility = "visible";

    const gameplayContent = document.getElementById(
      "right-panel-gameplay-content",
    );
    const gameplayTimer = document.getElementById("gameplay-timer");
    const gameplayPowers = document.getElementById("gameplay-powers");
    const resultsCounters = document.getElementById("results-counters");
    const resultsTotal = document.getElementById("results-total");

    if (gameplayContent) gameplayContent.style.display = "";
    if (gameplayTimer) gameplayTimer.style.display = "";
    if (gameplayPowers) gameplayPowers.style.display = "";
    if (resultsCounters) resultsCounters.style.display = "none";
    if (resultsTotal) resultsTotal.style.display = "none";

    this.add.image(400, 300, "bg").setDisplaySize(800, 600);
    this.stormOverlay = this.add
      .rectangle(400, 300, 800, 600, 0x444488, 0)
      .setDepth(999)
      .setScrollFactor(0);
    this.mudVisual = this.add
      .image(383, 580, "mud")
      .setDisplaySize(90, 70)
      .setDepth(1);

    this.mud = this.physics.add
      .staticImage(383, 580, "mud")
      .setDisplaySize(90, 70)
      .setVisible(false)
      .refreshBody();
    this.mud.body.checkCollision.none = false;

    this.mudSplashPlaying = false;

    // 🎭 MASK for immersion
    const maskGraphics = this.make.graphics();

    maskGraphics.fillStyle(0xffffff);
    maskGraphics.fillRect(0, 0, 800, this.mud.y - 20);

    this.playerMask = maskGraphics.createGeometryMask();

    this.levelMusic = this.sound.add("levelMusic", {
      loop: true,
      volume: Number(localStorage.getItem("agritech-music-volume") ?? "0.4"),
    });

    this.levelMusic.play();

    // 🏃 RUNNING SOUNDS
    const effectsVolume = Number(
      localStorage.getItem("agritech-effects-volume") ?? "0.6",
    );

    this.runSound = this.sound.add("running", {
      loop: true,
      volume: effectsVolume * 1,
    });

    this.runLeavesSound = this.sound.add("runningLeaves", {
      loop: true,
      volume: effectsVolume * 0.25,
    });

    this.isRunningSoundPlaying = false;
    this.isRunningLeavesPlaying = false;

    this.score = 0;
    this.blueCount = 0;
    this.greenCount = 0;
    this.starCount = 0;
    this.starScore = 0;
    this.starQuestionCount = 0;
    this.missedQuestions = [];
    this.vacuumActive = false;
    this.vacuumRadius = 250;
    this.vacuumStrength = 2500;
    this.bounceActive = false;
    this.bounceLock = false;

    // 🧠 track stored powerups
    this.usedPowerups = [];

    this.storedPowers = {
      vacuum: false,
      storm: false,
      bounce: false,
    };

    window.dispatchEvent(
      new CustomEvent("biorev-mobile-ui", {
        detail: {
          mode: "main",
          hideRightPanel: false,
          visibleControls: [
            "up",
            "left",
            "down",
            "right",
            "z",
            "x",
            "one",
            "two",
            "three",
            "p",
          ],
          powerStates: {
            one: false,
            two: false,
            three: false,
          },
        },
      }),
    );

    this.quizActive = false;

    this.t = getAgritechText();
    this.lang = getAgritechLanguage();

    this.quizQuestions =
      this.lang === "es" ? agritechQuestionsES : agritechQuestions;

    this.createGameplayControlsUI();

    // 🌪️ VACUUM PARTICLES
    this.vacuumParticles = this.add.particles(0, 0, "vacEffect", {
      speed: { min: 100, max: 200 },
      scale: { start: 0.1, end: 0 },
      lifespan: 500,
      quantity: 3,

      // 🌪️ SPAWN IN A CIRCLE AROUND PLANT
      emitZone: {
        type: "edge",
        source: new Phaser.Geom.Circle(0, 0, 60),
        quantity: 16,
      },

      // 💨 MOVE TOWARD CENTER
      moveToX: 0,
      moveToY: 0,
    });

    this.vacuumParticles.stop();

    // 👤 PLAYER
    this.playerLegacySpriteScale = 0.1;
    this.playerNewSpriteScale = 0.7;
    this.playerCrouchSpriteScale = 0.65;

    this.player = this.physics.add.sprite(100, 400, "walk0");
    this.player.setMask(this.playerMask);
    this.isDiving = false;
    this.isPounding = false;
    this.player.setCollideWorldBounds(true);
    this.player.setBounce(0);
    this.player.setDepth(10);

    this.syncPlayerSpriteSize();

    this.events.on(
      Phaser.Scenes.Events.POST_UPDATE,
      this.syncPlayerSpriteSize,
      this,
    );

    this.events.once("shutdown", () => {
      this.events.off(
        Phaser.Scenes.Events.POST_UPDATE,
        this.syncPlayerSpriteSize,
        this,
      );
    });

    // 🌱 PLANT HITBOX
    this.plant = this.physics.add.image(0, 0, null);
    this.plant.setVisible(false);
    this.plant.setDepth(20);

    this.plant.setDisplaySize(20, 20);
    this.plant.body.setSize(20, 20);
    this.plant.body.setAllowGravity(false);
    this.plant.body.setImmovable(true);

    // Visible while we verify the new sprite alignment.
    this.plantHitboxDebug = this.add
      .rectangle(0, 0, 20, 20, 0xff0000, 0.18)
      .setStrokeStyle(2, 0xff0000, 1)
      .setDepth(2000)
      .setVisible(false);

    // Luis Carlos Beltrán
    // 🧱 PLATFORMS
    this.platforms = this.physics.add.staticGroup();

    this.disappearingPlatform = this.platforms
      .create(740, 290, null)
      .setDisplaySize(120, 20)
      .setVisible(true)
      .refreshBody();

    this.disappearingPlatform2 = this.platforms
      .create(140, 330, null)
      .setDisplaySize(130, 20)
      .setVisible(true)
      .refreshBody();

    this.platforms
      .create(150, 570, null)
      .setDisplaySize(365, 80)
      .setVisible(false)
      .refreshBody();

    this.platforms
      .create(470, 580, null)
      .setDisplaySize(75, 150)
      .setVisible(false)
      .refreshBody();

    this.platforms
      .create(555, 565, null)
      .setDisplaySize(105, 310)
      .setVisible(false)
      .refreshBody();

    this.platforms
      .create(715, 550, null)
      .setDisplaySize(180, 120)
      .setVisible(false)
      .refreshBody();

    // 🌀 SPRING
    this.spring = this.physics.add.staticImage(455, 490, "spring1");
    this.spring.setScale(0.1);
    this.spring.setDepth(5);
    this.spring.refreshBody();

    this.springZone = this.add.rectangle(455, 500, 50, 10, 0xff0000, 0);
    this.physics.add.existing(this.springZone, true);
    this.springZone.setData("spring", this.spring);

    // 🌀 SPRING 2 (right side)
    this.spring2 = this.physics.add.staticImage(770, 480, "spring1");
    this.spring2.setScale(0.1);
    this.spring2.setDepth(5);
    this.spring2.refreshBody();

    // 🔥 trigger zone (same +10 Y offset)
    this.springZone2 = this.add.rectangle(770, 490, 50, 10, 0xff0000, 0);
    this.physics.add.existing(this.springZone2, true);
    this.springZone2.setData("spring", this.spring2);

    // 🎮 CONTROLS
    this.input.keyboard.enabled = true;
    this.input.keyboard.removeAllListeners();
    this.input.keyboard.removeAllKeys(true);

    this.mobileControls = {
      up: false,
      left: false,
      down: false,
      right: false,
      z: false,
      x: false,
      one: false,
      two: false,
      three: false,
      p: false,
    };

    this.mobileJustPressed = new Set();
    this.mobileJustReleased = new Set();

    this.consumeMobilePress = (control) => {
      if (!this.mobileJustPressed.has(control)) return false;
      this.mobileJustPressed.delete(control);
      return true;
    };

    this.consumeMobileRelease = (control) => {
      if (!this.mobileJustReleased.has(control)) return false;
      this.mobileJustReleased.delete(control);
      return true;
    };

    const makeMobileAwareKey = (keyboardKey, mobileControl) =>
      new Proxy(keyboardKey, {
        get: (target, property) => {
          if (property === "isDown") {
            return target.isDown || this.mobileControls[mobileControl];
          }

          return Reflect.get(target, property, target);
        },
      });

    const keyboardCursors = this.input.keyboard.createCursorKeys();

    this.cursors = {
      up: makeMobileAwareKey(keyboardCursors.up, "up"),
      left: makeMobileAwareKey(keyboardCursors.left, "left"),
      down: makeMobileAwareKey(keyboardCursors.down, "down"),
      right: makeMobileAwareKey(keyboardCursors.right, "right"),
    };

    const keyboardJumpKey = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.Z,
    );

    const keyboardDiveKey = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.X,
    );

    this.jumpKey = makeMobileAwareKey(keyboardJumpKey, "z");
    this.diveKey = makeMobileAwareKey(keyboardDiveKey, "x");

    // 🧪 DEBUG POWERUP KEYS
    this.power1Key = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.ONE,
    );

    this.power2Key = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.TWO,
    );

    this.power3Key = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.THREE,
    );

    this.handleMobileControl = (event) => {
      const control = event.detail?.control;
      const pressed = event.detail?.pressed === true;

      if (!Object.prototype.hasOwnProperty.call(this.mobileControls, control)) {
        return;
      }

      const wasDown = this.mobileControls[control];

      if (wasDown === pressed) return;

      this.mobileControls[control] = pressed;

      if (control === "p") {
        if (
          pressed &&
          !wasDown &&
          !this.quizActive &&
          typeof this.toggleManualPause === "function"
        ) {
          this.toggleManualPause();
        }

        return;
      }

      if (
        pressed &&
        !this.quizActive &&
        !this.manualPaused &&
        ["one", "two", "three"].includes(control)
      ) {
        this.mobileJustPressed.add(control);
      }

      if (!pressed && control === "z") {
        this.mobileJustReleased.add("z");
      }

      if (
        pressed &&
        !this.manualPaused &&
        ["left", "right", "down"].includes(control)
      ) {
        const now = performance.now();

        if (control === "left") {
          if (now - this.lastLeftTap < this.doubleTapDelay) {
            this.isDiving = true;
          }

          this.lastLeftTap = now;
        }

        if (control === "right") {
          if (now - this.lastRightTap < this.doubleTapDelay) {
            this.isDiving = true;
          }

          this.lastRightTap = now;
        }

        if (control === "down") {
          if (now - this.lastDownTap < this.doubleTapDelay) {
            this.downDoubleTap = true;
          }

          this.lastDownTap = now;
        }
      }
    };

    window.addEventListener("biorev-mobile-control", this.handleMobileControl);

    this.events.once("shutdown", () => {
      window.removeEventListener(
        "biorev-mobile-control",
        this.handleMobileControl,
      );
    });

    this.manualPaused = false;

    this.pauseOverlay = this.add
      .rectangle(400, 300, 800, 600, 0x06130c, 0.82)
      .setDepth(5000)
      .setScrollFactor(0)
      .setInteractive()
      .setVisible(false);

    this.pauseText = this.add
      .text(400, 270, this.lang === "es" ? "PAUSA" : "PAUSE", {
        fontFamily: '"Russo One", sans-serif',
        fontSize: "64px",
        color: "#ecfccb",
        stroke: "#14532d",
        strokeThickness: 5,
        letterSpacing: 4,
      })
      .setOrigin(0.5)
      .setDepth(5001)
      .setScrollFactor(0)
      .setVisible(false);

    this.pauseHint = this.add
      .text(
        400,
        335,
        this.lang === "es"
          ? "Presiona P para continuar"
          : "Press P to continue",
        {
          fontFamily: '"Montserrat", sans-serif',
          fontSize: "18px",
          color: "#d9f99d",
        },
      )
      .setOrigin(0.5)
      .setDepth(5001)
      .setScrollFactor(0)
      .setVisible(false);

    this.input.keyboard.on("keydown-P", () => {
      if (this.quizActive) return;
      this.toggleManualPause();
    });

    this.isJumping = false;
    this.dropThroughActive = false;
    this.dropThroughActive2 = false;

    this.lastLeftTap = 0;
    this.lastRightTap = 0;
    this.doubleTapDelay = 250; // ms
    this.lastDownTap = 0;
    this.downDoubleTap = false;

    this.input.keyboard.on("keydown-LEFT", () => {
      const now = this.time.now;

      if (now - this.lastLeftTap < this.doubleTapDelay) {
        this.isDiving = true;
      }

      this.lastLeftTap = now;
    });

    this.input.keyboard.on("keydown-DOWN", () => {
      const now = this.time.now;

      if (now - this.lastDownTap < this.doubleTapDelay) {
        this.downDoubleTap = true;
      }

      this.lastDownTap = now;
    });

    this.input.keyboard.on("keydown-RIGHT", () => {
      const now = this.time.now;

      if (now - this.lastRightTap < this.doubleTapDelay) {
        this.isDiving = true;
      }

      this.lastRightTap = now;
    });

    // 🎬 ANIMATIONS
    if (this.anims.exists("walk")) {
      this.anims.remove("walk");
    }

    if (this.anims.exists("run")) {
      this.anims.remove("run");
    }

    if (this.anims.exists("dive")) {
      this.anims.remove("dive");
    }

    this.anims.create({
      key: "walk",
      frames: [
        { key: "walk0" },
        { key: "walk1" },
        { key: "walk2" },
        { key: "walk3" },
        { key: "walk4" },
        { key: "walk5" },
        { key: "walk6" },
        { key: "walk7" },
      ],
      frameRate: 8,
      repeat: -1,
    });

    this.anims.create({
      key: "run",
      frames: [
        { key: "run1" },
        { key: "run2" },
        { key: "run3" },
        { key: "run4" },
        { key: "run5" },
      ],
      frameRate: 10,
      repeat: -1,
    });

    this.anims.create({
      key: "dive",
      frames: [
        { key: "dive1" },
        { key: "dive2" },
        { key: "dive3" },
        { key: "dive4" },
        { key: "dive5" },
      ],
      frameRate: 10,
      repeat: -1,
    });

    // 💥 MUD SPLATS (screen space)
    this.mudSplats = this.add.group();
    this.wasInMud = false;

    // 💧 DROPLETS
    this.droplets = this.physics.add.group();
    this.stars = this.physics.add.group();

    this.spawnEvent = this.time.addEvent({
      delay: 800,
      loop: true,
      callback: this.spawnDrop,
      callbackScope: this,
    });

    this.physics.add.overlap(
      this.plant,
      this.droplets,
      this.catchDrop,
      null,
      this,
    );

    this.physics.add.overlap(
      this.plant,
      this.stars,
      this.collectStar,
      null,
      this,
    );

    this.physics.add.collider(this.player, this.platforms);
    this.physics.add.collider(
      this.droplets,
      this.platforms,
      this.dropHitsPlatform,
      (drop, platform) =>
        platform !== this.disappearingPlatform &&
        platform !== this.disappearingPlatform2,
      this,
    );

    this.physics.add.overlap(
      this.player,
      this.springZone,
      this.hitSpring,
      null,
      this,
    );

    this.physics.add.overlap(
      this.player,
      this.springZone2,
      this.hitSpring,
      null,
      this,
    );

    // ⏱ TIMER
    this.timeLeft = 70;

    this.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => {
        this.timeLeft--;
        const timeEl = document.getElementById("ui-time");
        if (timeEl) timeEl.textContent = this.timeLeft;

        if (this.timeLeft <= 0) {
          this.levelMusic.stop();

          if (this.vacuumLoop) {
            this.vacuumLoop.stop();
          }

          if (this.runSound) {
            this.runSound.stop();
          }

          if (this.runLeavesSound) {
            this.runLeavesSound.stop();
          }

          window.dispatchEvent(
            new CustomEvent("biorev-finish-game-session", {
              detail: {
                finalScore: this.score,
                blueScore: this.blueCount * 100,
                greenScore: this.greenCount * 300,
                starScore: this.starScore,
                blueCount: this.blueCount,
                greenCount: this.greenCount,
                starCount: this.starCount,
              },
            }),
          );

          this.scene.start("TransitionScene", {
            label: this.t.finish,
            nextScene:
              this.missedQuestions.length > 0 ? "RecoveryScene" : "ResultScene",
            nextData: {
              score: this.score,
              blueCount: this.blueCount,
              greenCount: this.greenCount,
              starCount: this.starCount,
              blueScore: this.blueCount * 100,
              greenScore: this.greenCount * 300,
              starScore: this.starScore,
              missedQuestions: this.missedQuestions,
              language: this.lang,
            },
          });
        }
      },
    });
    this.time.delayedCall(1, () => {
      this.spawnStar("tier1");
    });

    this.time.delayedCall(25000, () => {
      this.spawnStar("tier2");
    });

    this.time.delayedCall(45000, () => {
      this.spawnStar("tier3");
    });

    window.dispatchEvent(new Event("biorev-game-ready"));
  }

  spawnDrop() {
    const x = Phaser.Math.Between(50, 750);
    const isGreen = Math.random() < 0.5;
    const key = isGreen ? "greendrop" : "bluedrop";
    const color = isGreen ? 0x66ff66 : 0x66ccff;

    this.createDropWarning(x, color);

    this.time.delayedCall(450, () => {
      const drop = this.physics.add.image(x, 0, key);
      drop.setBounce(this.bounceActive ? 1 : 0.2);
      drop.setCollideWorldBounds(true);

      drop.body.setGravityY(200);
      drop.setDamping(true);
      drop.setDrag(0.95);
      drop.setMaxVelocity(100);
      drop.setScale(0.05);

      drop.setData("type", isGreen ? "green" : "blue");

      this.droplets.add(drop);
    });
  }

  spawnStar(tier = "tier1") {
    const startX = Phaser.Math.Between(100, 700);
    const startY = Phaser.Math.Between(20, 80);

    const star = this.physics.add.image(startX, startY, "star");
    star.setScale(0.08);
    star.setData("tier", tier);

    // 🚫 keep physics overlap, but stop physics from moving the star
    star.body.setAllowGravity(false);
    star.body.setImmovable(true);
    star.body.setVelocity(0, 0);
    star.body.setCollideWorldBounds(false);
    star.body.moves = false;

    this.stars.add(star);

    const starSpawnTime = this.time.now;
    const dropDuration = 12000;

    const wander = () => {
      if (!star.active) return;

      const starAge = this.time.now - starSpawnTime;
      const elapsedRatio = Phaser.Math.Clamp(starAge / dropDuration, 0, 1);

      const yMin = Phaser.Math.Linear(20, 160, elapsedRatio);
      const yMax = Phaser.Math.Linear(180, 360, elapsedRatio);

      this.tweens.add({
        targets: star,
        x: Phaser.Math.Between(50, 750),
        y: Phaser.Math.Between(yMin, yMax),
        duration: Phaser.Math.Between(2000, 4000),
        ease: "sine.inOut",
        onComplete: wander,
      });
    };

    wander();
  }

  collectStar(plant, orb) {
    if (this.quizActive) return;

    orb.destroy();
    this.sound.play("powerGrab", {
      volume:
        Number(localStorage.getItem("agritech-effects-volume") ?? "0.6") * 0.5,
    });

    const available = ["vacuum", "storm", "bounce"].filter(
      (p) => !this.usedPowerups.includes(p),
    );

    if (available.length === 0) return;

    const choice = available[Math.floor(Math.random() * available.length)];

    this.starQuestionCount++;

    const tier =
      this.starQuestionCount === 1
        ? "tier1"
        : this.starQuestionCount === 2
          ? "tier2"
          : "tier3";

    this.openPowerQuestion(choice, tier);
  }

  activatePowerup(choice, x = this.plant.x, y = this.plant.y) {
    this.sound.play("apply", {
      volume:
        Number(localStorage.getItem("agritech-effects-volume") ?? "0.6") * 0.5,
    });

    if (choice === "vacuum") {
      this.vacuumActive = true;

      if (this.vacuumLoop) {
        this.vacuumLoop.stop();
      }
      this.vacuumLoop = this.sound.add("vacuumLoop", {
        loop: true,
        volume:
          Number(localStorage.getItem("agritech-effects-volume") ?? "0.6") *
          0.3,
      });

      this.vacuumLoop.play();

      this.time.delayedCall(15000, () => {
        this.vacuumActive = false;
        if (this.vacuumLoop) this.vacuumLoop.stop();
      });
    } else if (choice === "storm") {
      this.tripleRainActive = true;

      this.spawnEvent.delay = 200;

      this.sound.play("thunderclap", {
        volume:
          Number(localStorage.getItem("agritech-effects-volume") ?? "0.6") *
          0.6,
      });

      this.tweens.add({
        targets: this.stormOverlay,
        alpha: 0.4,
        duration: 200,
      });

      this.time.delayedCall(15000, () => {
        this.tripleRainActive = false;

        if (this.spawnEvent) {
          this.spawnEvent.delay = 800;
        }

        this.tweens.add({
          targets: this.stormOverlay,
          alpha: 0,
          duration: 300,
        });
      });
    } else if (choice === "bounce") {
      this.bounceActive = true;

      this.time.delayedCall(15000, () => {
        this.bounceActive = false;
      });
    }

    this.cameras.main.flash(200, 255, 255, 200);
    this.startPowerTimer(choice, 15);

    let label = this.lang === "es" ? "GRAVEDAD!" : "VACUUM!";

    if (choice === "storm") {
      label = this.lang === "es" ? "¡TORMENTA!" : "STORM!";
    } else if (choice === "bounce") {
      label = this.lang === "es" ? "GOMA!" : "BOUNCE!";
    }

    const txt = this.add
      .text(x, y, label, {
        fontSize: "28px",
        color: "#ffff00",
      })
      .setOrigin(0.5);

    this.tweens.add({
      targets: txt,
      y: txt.y - 20,
      alpha: 0,
      duration: 600,
      onComplete: () => txt.destroy(),
    });
  }

  pauseForQuestion() {
    this.quizActive = true;
    this.physics.pause();
    this.tweens.pauseAll();
    this.time.paused = true;
  }

  resumeFromQuestion() {
    this.time.paused = false;
    this.tweens.resumeAll();
    this.physics.resume();
    this.quizActive = false;
  }

  openPowerQuestion(choice, tier = "tier1") {
    const effectsVolume = Number(
      localStorage.getItem("agritech-effects-volume") ?? "0.6",
    );

    const playTap = () => {
      this.sound.play("tap", { volume: effectsVolume * 0.45 });
    };

    const playSelected = () => {
      this.sound.play("selected", { volume: effectsVolume * 0.5 });
    };

    const playCorrect = () => {
      this.sound.play("correct", { volume: effectsVolume * 0.6 });
    };

    const playIncorrect = () => {
      this.sound.play("incorrect", { volume: effectsVolume * 0.6 });
    };

    this.pauseForQuestion();

    const questionData = Phaser.Utils.Array.GetRandom(this.quizQuestions[tier]);

    const shuffledAnswers = Phaser.Utils.Array.Shuffle(
      questionData.answers.map((answer, originalIndex) => ({
        answer,
        isCorrect: originalIndex === questionData.correctIndex,
      })),
    );

    let selectedIndex = null;

    const powerLabel =
      choice === "vacuum"
        ? this.lang === "es"
          ? "Gravedad"
          : "Vacuum"
        : choice === "storm"
          ? this.lang === "es"
            ? "Tormenta"
            : "Storm"
          : this.lang === "es"
            ? "Goma"
            : "Bounce";

    const overlay = this.add
      .rectangle(400, 300, 800, 600, 0x000000, 0.72)
      .setDepth(3000)
      .setInteractive();

    const panel = this.add
      .rectangle(400, 300, 700, 500, 0x18231d, 0.98)
      .setStrokeStyle(3, 0x769b55)
      .setDepth(3001);

    const question = this.add
      .text(365, 110, questionData.question, {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "24px",
        color: "#ecfccb",
        fontStyle: "bold",
        align: "center",
        wordWrap: { width: 520 },
      })
      .setOrigin(0.5)
      .setDepth(3002);

    const timerX = 700;
    const timerY = 100;
    const timerRadius = 28;

    const timerGraphics = this.add.graphics().setDepth(3003);

    const countdownText = this.add
      .text(timerX, timerY, "", {
        fontFamily: '"Russo One", sans-serif',
        fontSize: "27px",
        color: "#a3e635",
      })
      .setOrigin(0.5)
      .setDepth(3004)
      .setVisible(false);

    const feedback = this.add
      .text(400, 480, "", {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "20px",
        color: "#f7faf4",
        fontStyle: "bold",
      })
      .setOrigin(0.5)
      .setDepth(3002);

    const finalButton = this.add
      .rectangle(400, 515, 240, 46, 0x405747, 1)
      .setDepth(3002);

    const finalText = this.add
      .text(400, 515, this.t.finalAnswer, {
        fontSize: "18px",
        color: "#9ca3af",
        fontStyle: "bold",
        fontFamily: '"Montserrat", sans-serif',
      })
      .setOrigin(0.5)
      .setDepth(3003);

    const modalItems = [
      overlay,
      panel,
      question,
      feedback,
      finalButton,
      finalText,
      timerGraphics,
      countdownText,
    ];

    const answerButtons = [];
    const QUESTION_TIME_MS = 35000;
    let questionTimerInterval = null;
    let countdownFlashTimeout = null;
    let timerExpired = false;
    let lastDisplayedSecond = null;

    const stopQuestionTimer = (removeVisuals = true) => {
      if (questionTimerInterval !== null) {
        window.clearInterval(questionTimerInterval);
        questionTimerInterval = null;
      }

      if (countdownFlashTimeout !== null) {
        window.clearTimeout(countdownFlashTimeout);
        countdownFlashTimeout = null;
      }

      if (removeVisuals) {
        if (timerGraphics?.active) timerGraphics.destroy();
        if (countdownText?.active) countdownText.destroy();
      }
    };

    const drawQuestionTimer = (remainingRatio, remainingSeconds) => {
      timerGraphics.clear();

      // Dim background ring
      timerGraphics.lineStyle(6, 0x4b5563, 0.75);
      timerGraphics.strokeCircle(timerX, timerY, timerRadius);

      // Remaining time drains counterclockwise
      if (remainingRatio > 0) {
        timerGraphics.lineStyle(
          6,
          remainingSeconds <= 5 ? 0xef4444 : 0xfacc15,
          1,
        );

        timerGraphics.beginPath();
        timerGraphics.arc(
          timerX,
          timerY,
          timerRadius,
          -Math.PI / 2,
          -Math.PI / 2 - Math.PI * 2 * remainingRatio,
          true,
        );
        timerGraphics.strokePath();
      }

      if (remainingSeconds <= 5) {
        countdownText
          .setText(String(remainingSeconds))
          .setColor(remainingSeconds <= 2 ? "#ef4444" : "#facc15")
          .setVisible(true);

        if (remainingSeconds !== lastDisplayedSecond) {
          lastDisplayedSecond = remainingSeconds;

          countdownText.setScale(1.45).setAlpha(1);

          if (countdownFlashTimeout !== null) {
            window.clearTimeout(countdownFlashTimeout);
          }

          countdownFlashTimeout = window.setTimeout(() => {
            if (countdownText?.active) {
              countdownText.setScale(1).setAlpha(0.9);
            }
          }, 180);
        }
      } else {
        countdownText.setVisible(false);
      }
    };

    const closeModal = () => {
      stopQuestionTimer();

      this.input.keyboard.off("keydown-ONE", selectOne);
      this.input.keyboard.off("keydown-TWO", selectTwo);
      this.input.keyboard.off("keydown-THREE", selectThree);
      this.input.keyboard.off("keydown-FOUR", selectFour);
      this.input.keyboard.off("keydown-ENTER", pressReturn);

      modalItems.forEach((item) => item.destroy());
      this.resumeFromQuestion();
    };

    const selectAnswer = (index) => {
      playSelected();

      if (feedback.text !== "") return;

      selectedIndex = index;

      answerButtons.forEach(({ button, keyBox, numberText, answerText }, i) => {
        if (i === selectedIndex) {
          button.setFillStyle(0x374151, 1);
          button.setStrokeStyle(2, 0xfacc15);
          keyBox.setFillStyle(0xfacc15, 1);
          keyBox.setStrokeStyle(1, 0xfacc15);
          numberText.setColor("#111827");
          answerText.setColor("#facc15");
        } else {
          button.setFillStyle(0x1f2937, 1);
          button.setStrokeStyle(1, 0x64748b);
          keyBox.setFillStyle(0x1f2937, 1);
          keyBox.setStrokeStyle(1, 0x64748b);
          numberText.setColor("#ffffff");
          answerText.setColor("#ffffff");
        }
      });

      finalButton.setFillStyle(0xfacc15, 1);
      finalButton.setInteractive({ useHandCursor: true });
      finalText.setColor("#111827");
    };

    const handleQuestionTimeout = () => {
      if (feedback.text !== "" || timerExpired) return;

      timerExpired = true;

      answerButtons.forEach(({ button }) => {
        button.disableInteractive();
      });

      finalButton.disableInteractive();

      this.missedQuestions.push({
        language: this.lang,
        power: choice,
        tier,
        question: questionData.question,
        answers: shuffledAnswers.map((answerData) => ({
          text: answerData.answer,
          correct: answerData.isCorrect,
        })),
        selectedAnswer: "",
        correctAnswer:
          shuffledAnswers.find((answerData) => answerData.isCorrect)?.answer ??
          "",
        scoreBeforeAnswer: this.score,
        timedOut: true,
      });

      window.dispatchEvent(
        new CustomEvent("biorev-record-answer", {
          detail: {
            language: this.lang,
            power: choice,
            tier,
            question: questionData.question,
            selectedAnswer: "",
            correctAnswer:
              shuffledAnswers.find((answer) => answer.isCorrect)?.answer ?? "",
            isCorrect: false,
            timedOut: true,
            scoreBeforeAnswer: this.score,
          },
        }),
      );

      feedback.setText(
        this.lang === "es" ? "¡Se acabó el tiempo!" : "Time's up!",
      );
      feedback.setColor("#fca5a5");

      finalText.setText(this.t.continue);
      finalButton.setFillStyle(0xfacc15, 1);
      finalText.setColor("#111827");
      finalButton.setInteractive({ useHandCursor: true });

      finalButton.once("pointerdown", () => {
        playSelected();
        closeModal();
      });
    };

    const submitAnswer = () => {
      playSelected();

      if (selectedIndex === null || feedback.text !== "" || timerExpired) {
        return;
      }

      stopQuestionTimer();

      answerButtons.forEach(({ button }) => {
        button.disableInteractive();
      });

      finalButton.disableInteractive();

      const selectedAnswer = shuffledAnswers[selectedIndex];
      const isCorrect = selectedAnswer.isCorrect;

      window.dispatchEvent(
        new CustomEvent("biorev-record-answer", {
          detail: {
            language: this.lang,
            power: choice,
            tier,
            question: questionData.question,
            selectedAnswer: selectedAnswer.answer,
            correctAnswer:
              shuffledAnswers.find((answer) => answer.isCorrect)?.answer ?? "",
            isCorrect,
            scoreBeforeAnswer: this.score,
          },
        }),
      );

      if (shuffledAnswers[selectedIndex].isCorrect) {
        playCorrect();

        this.score += 2000;
        this.starCount++;
        this.starScore += 2000;

        this.usedPowerups.push(choice);
        this.storedPowers[choice] = true;

        const scoreEl = document.getElementById("ui-score");
        const starCountEl = document.getElementById("ui-star-count");
        const starScoreEl = document.getElementById("ui-star-score");

        if (scoreEl) scoreEl.textContent = this.score;
        if (starCountEl) starCountEl.textContent = this.starCount;
        if (starScoreEl) starScoreEl.textContent = this.starScore;

        this.updatePowerUI();

        feedback.setText(
          this.lang === "es"
            ? `¡Correcto! +2000 puntos. Poder de ${powerLabel} guardado.`
            : `Correct! +2000 Points. ${powerLabel} power stored.`,
        );
        feedback.setColor("#86efac");
      } else {
        playIncorrect();

        this.missedQuestions.push({
          language: this.lang,
          power: choice,
          tier,
          question: questionData.question,
          answers: shuffledAnswers.map((answerData) => ({
            text: answerData.answer,
            correct: answerData.isCorrect,
          })),
          selectedAnswer: selectedAnswer.answer,
          correctAnswer:
            shuffledAnswers.find((answerData) => answerData.isCorrect)
              ?.answer ?? "",
          scoreBeforeAnswer: this.score,
        });

        feedback.setText(this.t.incorrect);
        feedback.setColor("#fca5a5");
      }

      finalText.setText(this.t.continue);
      finalButton.setFillStyle(0xfacc15, 1);
      finalText.setColor("#111827");
      finalButton.setInteractive({ useHandCursor: true });

      finalButton.once("pointerdown", () => {
        playSelected();
        closeModal();
      });
    };

    const selectOne = () => selectAnswer(0);
    const selectTwo = () => selectAnswer(1);
    const selectThree = () => selectAnswer(2);
    const selectFour = () => selectAnswer(3);

    const pressReturn = () => {
      if (feedback.text === "") {
        submitAnswer();
      } else {
        playSelected();
        closeModal();
      }
    };

    this.input.keyboard.on("keydown-ONE", selectOne);
    this.input.keyboard.on("keydown-TWO", selectTwo);
    this.input.keyboard.on("keydown-THREE", selectThree);
    this.input.keyboard.on("keydown-FOUR", selectFour);
    this.input.keyboard.on("keydown-ENTER", pressReturn);

    shuffledAnswers.forEach((answerData, index) => {
      const y = 185 + index * 72;

      const button = this.add
        .rectangle(400, y, 620, 58, 0x0b1510, 1)
        .setStrokeStyle(1, 0x405747)
        .setDepth(3002)
        .setInteractive({ useHandCursor: true });

      const keyBox = this.add
        .rectangle(115, y, 48, 58, 0x0b1510, 1)
        .setStrokeStyle(1, 0x405747)
        .setDepth(3002);

      const numberText = this.add
        .text(115, y, `${index + 1}`, {
          fontSize: "18px",
          color: "#ffffff",
          fontStyle: "bold",
          fontFamily: '"Montserrat", sans-serif',
        })
        .setOrigin(0.5)
        .setDepth(3003);

      const answerText = this.add
        .text(160, y, answerData.answer, {
          fontSize: "14px",
          color: "#ffffff",
          align: "left",
          wordWrap: { width: 520 },
          fontFamily: '"Montserrat", sans-serif',
        })
        .setOrigin(0, 0.5)
        .setDepth(3003);

      modalItems.push(button, keyBox, numberText, answerText);
      answerButtons.push({ button, keyBox, numberText, answerText });

      button.on("pointerover", () => {
        if (selectedIndex !== index && feedback.text === "") {
          playTap();
          button.setFillStyle(0x18291f, 1);
        }
      });

      button.on("pointerout", () => {
        if (selectedIndex !== index && feedback.text === "") {
          button.setFillStyle(0x0b1510, 1);
        }
      });

      button.on("pointerdown", () => {
        selectAnswer(index);
      });
    });

    finalButton.on("pointerdown", submitAnswer);

    const questionStartedAt = performance.now();

    drawQuestionTimer(1, QUESTION_TIME_MS / 1000);

    questionTimerInterval = window.setInterval(() => {
      const elapsed = performance.now() - questionStartedAt;
      const remainingMs = Math.max(0, QUESTION_TIME_MS - elapsed);
      const remainingRatio = remainingMs / QUESTION_TIME_MS;
      const remainingSeconds =
        remainingMs <= 0 ? 0 : Math.ceil(remainingMs / 1000);

      drawQuestionTimer(remainingRatio, remainingSeconds);

      if (remainingMs <= 0) {
        stopQuestionTimer(false);
        handleQuestionTimeout();
      }
    }, 50);
  }

  catchDrop(plant, drop) {
    const type = drop.getData("type");

    if (type === "green") {
      this.greenCount++;
      this.score += 300;
      this.sound.play("greenSound", {
        volume:
          Number(localStorage.getItem("agritech-effects-volume") ?? "0.6") * 1,
      });
    } else {
      this.blueCount++;
      this.score += 100;
      this.sound.play("blueSound", {
        volume:
          Number(localStorage.getItem("agritech-effects-volume") ?? "0.6") * 1,
      });
    }

    const scoreEl = document.getElementById("ui-score");
    const blueCountEl = document.getElementById("ui-blue-count");
    const greenCountEl = document.getElementById("ui-green-count");
    const blueScoreEl = document.getElementById("ui-blue-score");
    const greenScoreEl = document.getElementById("ui-green-score");

    if (scoreEl) scoreEl.textContent = this.score;

    if (blueCountEl) blueCountEl.textContent = this.blueCount;
    if (greenCountEl) greenCountEl.textContent = this.greenCount;

    if (blueScoreEl) blueScoreEl.textContent = this.blueCount * 100;
    if (greenScoreEl) greenScoreEl.textContent = this.greenCount * 300;

    // ✨ FLOATING SCORE TEXT
    const points = type === "green" ? 300 : 100;

    const floatText = this.add
      .text(drop.x, drop.y, `+${points}`, {
        fontSize: "16px",
        color: "#ffffff",
      })
      .setOrigin(0.5);

    // animate upward + fade
    this.tweens.add({
      targets: floatText,
      y: floatText.y - 20,
      alpha: 0,
      duration: 600,
      ease: "sine.out",
      onComplete: () => floatText.destroy(),
    });

    drop.destroy();
  }

  updatePowerUI() {
    const vacuumRow = document.getElementById("ui-vacuum-row");
    const stormRow = document.getElementById("ui-storm-row");
    const bounceRow = document.getElementById("ui-bounce-row");

    const vacuumKey = document.getElementById("ui-vacuum-key");
    const stormKey = document.getElementById("ui-storm-key");
    const bounceKey = document.getElementById("ui-bounce-key");

    if (
      !vacuumRow ||
      !stormRow ||
      !bounceRow ||
      !vacuumKey ||
      !stormKey ||
      !bounceKey
    ) {
      return;
    }

    const setPowerState = (row, key, active) => {
      if (active) {
        row.className = "flex items-center gap-3 text-yellow-300";
        key.className =
          "relative w-10 h-10 rounded-lg border border-yellow-300 bg-yellow-400 text-[#111827] flex items-center justify-center text-lg font-bold animate-pulse shadow-lg overflow-hidden";
      } else {
        row.className = "flex items-center gap-3 text-gray-500";
        key.className =
          "relative w-10 h-10 rounded-lg border border-gray-600 bg-gray-800 flex items-center justify-center text-lg font-bold overflow-hidden";
      }
    };

    setPowerState(vacuumRow, vacuumKey, this.storedPowers.vacuum);
    setPowerState(stormRow, stormKey, this.storedPowers.storm);
    setPowerState(bounceRow, bounceKey, this.storedPowers.bounce);

    window.dispatchEvent(
      new CustomEvent("biorev-mobile-ui", {
        detail: {
          mode: "main",
          hideRightPanel: false,
          visibleControls: [
            "up",
            "left",
            "down",
            "right",
            "z",
            "x",
            "one",
            "two",
            "three",
            "p",
          ],
          powerStates: {
            one: this.storedPowers.vacuum === true,
            two: this.storedPowers.storm === true,
            three: this.storedPowers.bounce === true,
          },
        },
      }),
    );
  }

  startPowerTimer(powerName, durationSeconds = 15) {
    const timerEl = document.getElementById(`ui-${powerName}-timer`);
    if (!timerEl) return;

    if (!this.powerTimerEvents) {
      this.powerTimerEvents = {};
    }

    if (this.powerTimerEvents[powerName]) {
      this.powerTimerEvents[powerName].remove(false);
    }

    let timeLeft = durationSeconds;

    timerEl.textContent = timeLeft;
    timerEl.className =
      "absolute inset-0 bg-black/60 text-white flex items-center justify-center text-sm font-bold";

    this.powerTimerEvents[powerName] = this.time.addEvent({
      delay: 1000,
      repeat: durationSeconds - 1,
      callback: () => {
        timeLeft--;

        if (timeLeft > 0) {
          timerEl.textContent = timeLeft;
        } else {
          timerEl.textContent = "";
          timerEl.className =
            "hidden absolute inset-0 bg-black/60 text-white flex items-center justify-center text-sm font-bold";
          this.powerTimerEvents[powerName] = null;
        }
      },
    });
  }

  createGameplayControlsUI() {
    const panel = document.getElementById("left-ui-panel");
    if (!panel) return;

    const controlsLabel = this.lang === "es" ? "Controles" : "Controls";
    const jumpLabel = this.lang === "es" ? "Saltar" : "Jump";
    const diveRunLabel = this.lang === "es" ? "Planear / Correr" : "Dive / Run";

    const existing = document.getElementById("main-scene-controls-ui");

    if (existing) {
      existing.remove();
    }

    const wrapper = document.createElement("div");

    wrapper.id = "main-scene-controls-ui";
    wrapper.className = "mt-auto";

    wrapper.innerHTML = `
    <p class="text-sm text-gray-400 mb-5 text-center">
      ${controlsLabel}
    </p>

    <div class="flex justify-center gap-6 mb-8">
      <div class="flex flex-col items-center gap-2">
        <div
          id="ui-key-z"
          class="w-12 h-12 rounded-lg border border-gray-600 bg-gray-800 text-gray-500 flex items-center justify-center text-xl font-bold"
        >
          Z
        </div>

        <p class="text-xs text-gray-500 text-center">
          ${jumpLabel}
        </p>
      </div>

      <div class="flex flex-col items-center gap-2">
        <div
          id="ui-key-x"
          class="w-12 h-12 rounded-lg border border-gray-600 bg-gray-800 text-gray-500 flex items-center justify-center text-xl font-bold"
        >
          X
        </div>

        <p class="text-xs text-gray-500 text-center">
          ${diveRunLabel}
        </p>
      </div>
    </div>

    <div class="flex flex-col items-center gap-3">
      <div
        id="ui-key-up"
        class="w-12 h-12 rounded-lg border border-gray-600 bg-gray-800 text-gray-500 flex items-center justify-center text-xl font-bold"
      >
        ↑
      </div>

      <div class="flex gap-3">
        <div
          id="ui-key-left"
          class="w-12 h-12 rounded-lg border border-gray-600 bg-gray-800 text-gray-500 flex items-center justify-center text-xl font-bold"
        >
          ←
        </div>

        <div
          id="ui-key-down"
          class="w-12 h-12 rounded-lg border border-gray-600 bg-gray-800 text-gray-500 flex items-center justify-center text-xl font-bold"
        >
          ↓
        </div>

        <div
          id="ui-key-right"
          class="w-12 h-12 rounded-lg border border-gray-600 bg-gray-800 text-gray-500 flex items-center justify-center text-xl font-bold"
        >
          →
        </div>
      </div>
    </div>
  `;

    panel.appendChild(wrapper);

    this.events.once("shutdown", () => {
      wrapper.remove();
    });
  }

  updateControlUI() {
    const up = document.getElementById("ui-key-up");
    const left = document.getElementById("ui-key-left");
    const down = document.getElementById("ui-key-down");
    const right = document.getElementById("ui-key-right");
    const z = document.getElementById("ui-key-z");
    const x = document.getElementById("ui-key-x");

    if (!up || !left || !down || !right || !z || !x) return;

    const offClass =
      "w-12 h-12 rounded-lg border border-gray-600 bg-gray-800 text-gray-500 flex items-center justify-center text-xl font-bold";

    const onClass =
      "w-12 h-12 rounded-lg border border-white bg-white text-[#111827] flex items-center justify-center text-xl font-bold shadow-lg shadow-white/30";

    up.className = this.cursors.up.isDown ? onClass : offClass;
    left.className = this.cursors.left.isDown ? onClass : offClass;
    down.className = this.cursors.down.isDown ? onClass : offClass;
    right.className = this.cursors.right.isDown ? onClass : offClass;
    z.className = this.jumpKey.isDown ? onClass : offClass;
    x.className = this.diveKey.isDown ? onClass : offClass;
  }

  dropHitsMud(drop, mud) {
    if (!drop.active) return;

    // 🧹 REMOVE FROM PHYSICS GROUP IMMEDIATELY
    this.droplets.remove(drop, false, false);

    // 💥 effects
    this.spawnMudDrops();
    this.playMudImpact();
    this.sound.play("mudSplash1", {
      volume:
        Number(localStorage.getItem("agritech-effects-volume") ?? "0.6") * 0.4,
    });
    // ❌ destroy instantly (no delay)
    drop.destroy();
  }

  dropHitsPlatform(drop, platform) {
    if (!drop.active) return;

    const x = drop.x;
    const y = drop.y;

    if (this.bounceActive) {
      const now = this.time.now;
      const lastBounceFx = drop.getData("lastBounceFx") || 0;
      const bounceFxCount = drop.getData("bounceFxCount") || 0;

      if (bounceFxCount < 5 && now - lastBounceFx > 80) {
        const type = drop.getData("type");
        const color = type === "green" ? 0x66ff66 : 0x66ccff;

        this.createDropBounceWarp(x, y, color);
        drop.setData("lastBounceFx", now);
        drop.setData("bounceFxCount", bounceFxCount + 1);
      }

      return;
    }

    // 💥 spawn small fragments
    const fragments = 4;

    for (let i = 0; i < fragments; i++) {
      const piece = this.add
        .image(x, y, drop.texture.key)
        .setScale(0.03)
        .setDepth(50);

      const angle = Phaser.Math.FloatBetween(0, Math.PI * 2);

      this.tweens.add({
        targets: piece,
        x: x + Math.cos(angle) * 40,
        y: y + Math.sin(angle) * 40,
        alpha: 0,
        duration: 300,
        ease: "quad.out",
        onComplete: () => piece.destroy(),
      });
    }

    this.sound.play("mudSplash2", {
      volume:
        Number(localStorage.getItem("agritech-effects-volume") ?? "0.6") * 0.2,
    });

    drop.destroy();
  }

  hitSpring(player, zone) {
    const springTop = zone.y - zone.height / 2;
    const playerBottom = player.body.bottom;
    const prevBottom = player.body.prev.y + player.body.height;

    // only trigger if falling AND crossing the top of the spring
    if (
      player.body.velocity.y <= 0 || // not falling
      prevBottom > springTop || // was already below top
      playerBottom < springTop - 5 // not actually landing on it
    ) {
      return;
    }
    if (this.springActive) return;

    this.springActive = true;
    this.sound.play("springSound", {
      volume:
        Number(localStorage.getItem("agritech-effects-volume") ?? "0.6") * 0.75,
    });

    const spring = zone.getData("spring");

    player.setVelocityY(-475);

    this.isDiving = false;
    this.isPounding = false;
    this.springLock = true;

    this.player.anims.stop();
    this.player.setTexture("walk0");

    // ⏱ unlock after 500 ms
    this.time.delayedCall(500, () => {
      this.springLock = false;
    });

    spring.setTexture("spring2");

    this.time.delayedCall(50, () => {
      spring.setTexture("spring3");
    });

    this.time.delayedCall(100, () => {
      spring.setTexture("spring1");
      this.springActive = false;
    });
  }

  playMudImpact() {
    if (!this.mudVisual || !this.mudVisual.active) return;
    if (this.mudSplashPlaying) return;

    this.mudSplashPlaying = true;
    this.mudVisual.setTexture("mud2");

    this.mudVisual.setTexture("mud2");

    this.time.delayedCall(120, () => {
      if (this.mudVisual && this.mudVisual.active) {
        this.mudVisual.setTexture("mud");
      }

      this.mudSplashPlaying = false;
    });
  }

  createBounceWarp(x, y) {
    this.cameras.main.shake(80, 0.004);

    const warp = this.add
      .ellipse(x, y - 25, 30, 10, 0xffffff, 0.22)
      .setDepth(998)
      .setScale(1)
      .setAlpha(0.7);

    const ring = this.add
      .ellipse(x, y - 25, 40, 12)
      .setStrokeStyle(3, 0xffffff, 0.55)
      .setDepth(998)
      .setScale(1)
      .setAlpha(0.8);

    this.tweens.add({
      targets: warp,
      scaleX: 3.2,
      scaleY: 0.45,
      alpha: 0,
      duration: 220,
      ease: "sine.out",
      onComplete: () => warp.destroy(),
    });

    this.tweens.add({
      targets: ring,
      scaleX: 3.8,
      scaleY: 0.6,
      alpha: 0,
      duration: 260,
      ease: "sine.out",
      onComplete: () => ring.destroy(),
    });
  }

  createDropWarning(x, color) {
    const warning = this.add
      .triangle(x, 18, 0, 18, 12, 0, 24, 18, color, 0.75)
      .setDepth(1001)
      .setOrigin(0.5);

    this.tweens.add({
      targets: warning,
      alpha: 0.15,
      yoyo: true,
      repeat: 3,
      duration: 90,
      ease: "sine.inOut",
      onComplete: () => warning.destroy(),
    });
  }

  createDropBounceWarp(x, y, color) {
    const warp = this.add
      .ellipse(x, y - 1, 14, 5, color, 0.22)
      .setDepth(998)
      .setScale(1)
      .setAlpha(0.65);

    const ring = this.add
      .ellipse(x, y - 1, 18, 6)
      .setStrokeStyle(2, color, 0.55)
      .setDepth(998)
      .setScale(1)
      .setAlpha(0.75);

    this.tweens.add({
      targets: warp,
      scaleX: 2.2,
      scaleY: 0.45,
      alpha: 0,
      duration: 160,
      ease: "sine.out",
      onComplete: () => warp.destroy(),
    });

    this.tweens.add({
      targets: ring,
      scaleX: 2.6,
      scaleY: 0.55,
      alpha: 0,
      duration: 190,
      ease: "sine.out",
      onComplete: () => ring.destroy(),
    });
  }

  spawnMudDrops(source = this.player) {
    const impact = Phaser.Math.Clamp(
      Math.abs(source.body?.velocity?.y || this.player.body.velocity.y) / 600,
      0.3,
      1,
    );
    const splashCount = Math.floor(Phaser.Math.Linear(5, 12, impact));

    for (let i = 0; i < splashCount; i++) {
      // 🎯 TARGET (where splat will appear)
      const targetX = Phaser.Math.Between(50, 750);
      const targetY = Phaser.Math.Between(50, 550);

      // 🌋 START FROM MUD POSITION
      const startX = this.mud.x + Phaser.Math.Between(-20, 20);
      const startY = this.mud.y - 10;

      const drop = this.add
        .image(startX, startY, "brownDrop")
        .setScale(Phaser.Math.FloatBetween(0.05, 0.12))
        .setDepth(999)
        .setScrollFactor(1);

      // 🚀 ARC MOTION
      const distance = Phaser.Math.Distance.Between(
        startX,
        startY,
        targetX,
        targetY,
      );

      // pixels per second (tune this)
      const speed = 1200;

      // duration based on distance
      const duration = (distance / speed) * 1000;

      this.tweens.add({
        targets: drop,
        x: targetX,
        y: targetY,
        duration: duration,
        ease: "quad.out",

        onComplete: () => {
          this.sound.play("mudSplash2", {
            volume:
              Number(localStorage.getItem("agritech-effects-volume") ?? "0.6") *
              0.3,
          }); // 💥 CREATE FINAL SPLAT
          const scale = Phaser.Math.FloatBetween(0.08, 0.25) * (0.6 + impact);

          const splat = this.add
            .image(targetX, targetY, "mudSplash")
            .setScale(scale)
            .setAlpha(Phaser.Math.FloatBetween(0.4, 0.8))
            .setDepth(1000)
            .setScrollFactor(1);

          this.mudSplats.add(splat);

          // 🟤 OOZE
          this.tweens.add({
            targets: splat,
            y: targetY + Phaser.Math.Between(20, 80),
            scaleY: scale * 1.3,
            duration: Phaser.Math.Between(4000, 9000),
            ease: "sine.out",
          });

          // 🌫️ FADE
          this.tweens.add({
            targets: splat,
            alpha: 0,
            duration: 10000,
            ease: "linear",
            onComplete: () => splat.destroy(),
          });

          drop.destroy();
        },
      });
    }
  }
  toggleManualPause() {
    this.manualPaused = !this.manualPaused;

    if (this.manualPaused) {
      this.physics.pause();
      this.tweens.pauseAll();
      this.time.paused = true;
      this.sound.pauseAll();

      this.pauseOverlay.setVisible(true);
      this.pauseText.setVisible(true);
      this.pauseHint.setVisible(true);
    } else {
      this.time.paused = false;
      this.tweens.resumeAll();
      this.physics.resume();
      this.sound.resumeAll();

      this.pauseOverlay.setVisible(false);
      this.pauseText.setVisible(false);
      this.pauseHint.setVisible(false);
    }
  }

  syncPlayerSpriteSize() {
    const player = this.player;
    if (!player || !player.body) return;

    const textureKey = player.texture?.key || "";

    const targetScale =
      textureKey === "crouch"
        ? this.playerCrouchSpriteScale
        : this.playerNewSpriteScale;

    const textureChanged = this.playerLastTextureKey !== textureKey;

    const scaleChanged =
      Math.abs(player.scaleX - targetScale) >= 0.000001 ||
      Math.abs(player.scaleY - targetScale) >= 0.000001;

    if (!textureChanged && !scaleChanged) {
      return;
    }

    player.setScale(targetScale);

    // Establish the collision size once from walk0 and keep it constant
    // while the visible texture and scale change.
    if (
      this.playerBodyWorldWidth === undefined ||
      this.playerBodyWorldHeight === undefined
    ) {
      this.playerBodyWorldWidth = player.width * 0.6 * targetScale;
      this.playerBodyWorldHeight = player.height * 0.8 * targetScale;
    }

    player.body.setSize(
      this.playerBodyWorldWidth / targetScale,
      this.playerBodyWorldHeight / targetScale,
      true,
    );

    this.playerLastTextureKey = textureKey;
  }

  update() {
    if (this.manualPaused) return;
    if (this.quizActive) return;

    const mobileJumpReleased = this.consumeMobileRelease("z");

    if (
      (Phaser.Input.Keyboard.JustDown(this.power1Key) ||
        this.consumeMobilePress("one")) &&
      this.storedPowers.vacuum
    ) {
      this.activatePowerup("vacuum");
      this.storedPowers.vacuum = false;
      this.updatePowerUI();
    }

    if (
      (Phaser.Input.Keyboard.JustDown(this.power2Key) ||
        this.consumeMobilePress("two")) &&
      this.storedPowers.storm
    ) {
      this.activatePowerup("storm");
      this.storedPowers.storm = false;
      this.updatePowerUI();
    }

    if (
      (Phaser.Input.Keyboard.JustDown(this.power3Key) ||
        this.consumeMobilePress("three")) &&
      this.storedPowers.bounce
    ) {
      this.activatePowerup("bounce");
      this.storedPowers.bounce = false;
      this.updatePowerUI();
    }

    this.updateControlUI();

    // only stop horizontal movement when NOT bouncing

    // only stop horizontal movement when NOT bouncing
    if (!this.bounceLock && !this.bounceActive) {
      this.player.setVelocityX(0);
    }
    this.player.setBounce(0);
    this.player.setBounce(0);
    // ⬇️ DROP THROUGH SPECIFIC PLATFORM
    const playerBottom = this.player.body.bottom;
    const onGround = this.player.body.blocked.down;

    const onFloatingPlatform =
      onGround &&
      this.disappearingPlatform.body.enable &&
      Math.abs(playerBottom - this.disappearingPlatform.body.top) < 5;

    const onFloatingPlatform2 =
      onGround &&
      this.disappearingPlatform2.body.enable &&
      Math.abs(playerBottom - this.disappearingPlatform2.body.top) < 5;

    const onLeaves = onFloatingPlatform || onFloatingPlatform2;

    const inMud = playerBottom > this.mud.y - 10;

    // track if we were falling LAST frame
    const wasFalling = this.wasFalling || false;
    const isFalling = this.player.body.velocity.y > 0;

    // 🎯 TRUE LANDING: was falling → now grounded
    const justLanded = onGround && !this.wasOnGround && wasFalling;
    if (this.bounceActive && justLanded && !this.bounceLock) {
      this.createBounceWarp(this.player.x, this.player.body.bottom);

      this.player.setVelocityY(-350);
      this.sound.play("bounceClip", {
        volume:
          Number(localStorage.getItem("agritech-effects-volume") ?? "0.6") * 5,
      });
      this.bounceLock = true;
      this.isPounding = false;
      this.isDiving = false;
      this.downDoubleTap = false;

      this.time.delayedCall(200, () => {
        this.bounceLock = false;
      });
    }
    if (inMud && justLanded) {
      this.spawnMudDrops(this.player);
      this.playMudImpact();
      this.sound.play("mudSplash1", {
        volume:
          Number(localStorage.getItem("agritech-effects-volume") ?? "0.6") *
          0.6,
      });
    }

    // update trackers
    this.wasOnGround = onGround;
    this.wasInMud = inMud;
    this.wasFalling = isFalling;

    const platformTop = this.disappearingPlatform.body.top;

    // detect "standing on THIS platform" using position + grounded
    const standingOnPlatform =
      this.player.body.blocked.down && Math.abs(playerBottom - platformTop) < 5;

    if (standingOnPlatform && this.downDoubleTap && !this.dropThroughActive) {
      this.dropThroughActive = true;
      this.downDoubleTap = false;

      // disable ONLY this platform
      this.disappearingPlatform.body.enable = false;

      // re-enable after 1 second
      this.time.delayedCall(1000, () => {
        this.disappearingPlatform.body.enable = true;
        this.dropThroughActive = false;
      });
    }

    const platformTop2 = this.disappearingPlatform2.body.top;

    const standingOnPlatform2 =
      this.player.body.blocked.down &&
      Math.abs(playerBottom - platformTop2) < 5;

    if (standingOnPlatform2 && this.downDoubleTap && !this.dropThroughActive2) {
      this.dropThroughActive2 = true;
      this.downDoubleTap = false;

      this.disappearingPlatform2.body.enable = false;

      this.time.delayedCall(1000, () => {
        this.disappearingPlatform2.body.enable = true;
        this.dropThroughActive2 = false;
      });
    }

    // Hide when player feet is BELOW the platform
    // PLATFORM 1
    if (!this.dropThroughActive) {
      if (playerBottom < platformTop + 10) {
        // feet are ABOVE the platform → show it
        this.disappearingPlatform.setVisible(false); // change to see it
        this.disappearingPlatform.body.enable = true;
      } else {
        // feet are still below (or inside) → hide it
        this.disappearingPlatform.setVisible(false);
        this.disappearingPlatform.body.enable = false;
      }
    }

    // PLATFORM 2
    if (!this.dropThroughActive2) {
      if (playerBottom < this.disappearingPlatform2.body.top + 10) {
        this.disappearingPlatform2.setVisible(false); // change to see it
        this.disappearingPlatform2.body.enable = true;
      } else {
        this.disappearingPlatform2.setVisible(false);
        this.disappearingPlatform2.body.enable = false;
      }
    }

    const walkSpeed = 200;
    const runSpeed = 375;

    const isRunning =
      this.diveKey.isDown &&
      onGround &&
      (this.cursors.left.isDown || this.cursors.right.isDown);

    // ✂️ VARIABLE JUMP CUT
    if (
      this.isJumping &&
      (Phaser.Input.Keyboard.JustUp(this.jumpKey) || mobileJumpReleased)
    ) {
      if (this.player.body.velocity.y < 0) {
        this.player.setVelocityY(this.player.body.velocity.y * 0.5);
      }
      this.isJumping = false;
    }

    if (onGround) {
      this.isDiving = false;
      this.isJumping = false;
      this.isPounding = false;
      this.springLock = false;
    }

    // ⬆️ JUMP
    if (this.jumpKey.isDown && onGround) {
      this.player.setVelocityY(-300);
      this.isJumping = true;
    }

    // ⬇️ POUND
    if (
      this.cursors.down.isDown &&
      !onGround &&
      !this.springLock &&
      !this.bounceLock
    ) {
      this.isPounding = true;
      this.isDiving = false;

      this.player.anims.stop();
      this.player.setTexture("pound1");
      this.player.setVelocityY(400);

      if (!this.bounceLock && this.cursors.left.isDown) {
        this.player.setVelocityX(-walkSpeed);
        this.player.setFlipX(false);
      } else if (!this.bounceLock && this.cursors.right.isDown) {
        this.player.setVelocityX(walkSpeed);
        this.player.setFlipX(true);
      }

      // 🪂 DIVE
    } else if (this.diveKey.isDown && !onGround) {
      this.isDiving = true;
      this.isPounding = false;

      this.player.anims.play("dive", true);

      if (!this.bounceLock && this.cursors.left.isDown) {
        this.player.setVelocityX(-runSpeed);
        this.player.setFlipX(false);
      } else if (!this.bounceLock && this.cursors.right.isDown) {
        this.player.setVelocityX(runSpeed);
        this.player.setFlipX(true);
      }

      // 🪂 AIR STATE (non-dive jump sprite)
    } else if (!onGround) {
      this.isDiving = false;
      this.isPounding = false;

      this.player.anims.stop();

      if (this.cursors.up.isDown) {
        this.player.setTexture("jumpup");
      } else {
        this.player.setTexture(
          this.player.body.velocity.y < 0 ? "jump1" : "jump2",
        );
      }

      if (!this.bounceLock && this.cursors.left.isDown) {
        this.player.setVelocityX(-walkSpeed);
        this.player.setFlipX(false);
      } else if (!this.bounceLock && this.cursors.right.isDown) {
        this.player.setVelocityX(walkSpeed);
        this.player.setFlipX(true);
      }

      // 🚶 / 🏃 GROUND LEFT
    } else if (!this.bounceLock && this.cursors.left.isDown) {
      this.isDiving = false;
      this.isPounding = false;

      if (this.diveKey.isDown) {
        this.player.setVelocityX(-runSpeed);
        this.player.anims.play("run", true);
      } else {
        this.player.setVelocityX(-walkSpeed);
        this.player.anims.play("walk", true);
      }

      this.player.setFlipX(false);

      // 🚶 / 🏃 GROUND RIGHT
    } else if (!this.bounceLock && this.cursors.right.isDown) {
      this.isDiving = false;
      this.isPounding = false;

      if (this.diveKey.isDown) {
        this.player.setVelocityX(runSpeed);
        this.player.anims.play("run", true);
      } else {
        this.player.setVelocityX(walkSpeed);
        this.player.anims.play("walk", true);
      }

      this.player.setFlipX(true);

      // 🧍 IDLE / CROUCH / LOOK UP
    } else {
      this.isDiving = false;
      this.isPounding = false;

      this.player.anims.stop();

      if (onGround && this.cursors.down.isDown) {
        this.player.setTexture("crouchFinal");
      } else if (onGround && this.cursors.up.isDown) {
        this.player.setTexture("up");
      } else {
        this.player.setTexture("walk0");
      }
    }

    // 🌱 PLANT POSITION
    let offsetX = this.player.flipX ? 28 : -28;
    let offsetY = 0;

    if (this.player.texture.key.startsWith("dive")) {
      offsetX = this.player.flipX ? 52 : -52;
    }

    if (this.player.texture.key === "pound1") {
      offsetX = 0;
      offsetY = -this.player.body.height * 0.6;
    }

    if (this.player.texture.key === "jumpup") {
      offsetX = this.player.flipX ? 6 : 2;
      offsetY = -this.player.body.height * 0.6;
    }

    if (this.player.texture.key === "crouch") {
      offsetX = 2;
      offsetY = -this.player.body.height * 0.6;
    }

    if (this.player.texture.key === "up") {
      offsetX = 2;
      offsetY = -this.player.body.height * 0.65;
    }

    this.plant.setPosition(
      this.player.body.center.x + offsetX,
      this.player.body.center.y + offsetY,
    );

    if (this.plantHitboxDebug) {
      this.plantHitboxDebug.setPosition(this.plant.x, this.plant.y);
    }
    // 🎧 RUNNING SOUND CONTROL
    if (isRunning) {
      if (onLeaves) {
        // play leaves sound
        if (!this.isRunningLeavesPlaying) {
          this.runLeavesSound.play();
          this.isRunningLeavesPlaying = true;
        }

        if (this.isRunningSoundPlaying) {
          this.runSound.stop();
          this.isRunningSoundPlaying = false;
        }
      } else {
        // play normal ground running
        if (!this.isRunningSoundPlaying) {
          this.runSound.play();
          this.isRunningSoundPlaying = true;
        }

        if (this.isRunningLeavesPlaying) {
          this.runLeavesSound.stop();
          this.isRunningLeavesPlaying = false;
        }
      }
    } else {
      // stop all if not running
      if (this.isRunningSoundPlaying) {
        this.runSound.stop();
        this.isRunningSoundPlaying = false;
      }

      if (this.isRunningLeavesPlaying) {
        this.runLeavesSound.stop();
        this.isRunningLeavesPlaying = false;
      }
    }

    this.vacuumParticles.setPosition(this.plant.x, this.plant.y);
    if (this.vacuumActive) {
      this.vacuumParticles.start();
    } else {
      this.vacuumParticles.stop();
    }

    this.droplets.children.iterate((drop) => {
      if (!drop) return;
      drop.setBounce(this.bounceActive ? 0.8 : 0.8);
      if (
        drop.active &&
        !drop.getData("hitMud") &&
        Phaser.Geom.Intersects.RectangleToRectangle(
          drop.getBounds(),
          this.mud.getBounds(),
        )
      ) {
        drop.setData("hitMud", true);
        this.dropHitsMud(drop, this.mud);
        return;
      }

      if (!this.vacuumActive) {
        drop.setAcceleration(0, 0);
        return;
      }

      const dx = this.plant.x - drop.x;
      const dy = this.plant.y - drop.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < this.vacuumRadius) {
        if (dist < 40) {
          drop.setVelocity(0, 0);
          drop.setPosition(this.plant.x, this.plant.y);
          return;
        }
        const nx = dx / dist;
        const ny = dy / dist;

        // keep your existing pull
        const t = 1 - dist / this.vacuumRadius;
        const strength = this.vacuumStrength * (t * t);

        drop.setAcceleration(nx * strength, ny * strength);

        // 🔥 FUNNEL: steer velocity toward center
        const vx = drop.body.velocity.x;
        const vy = drop.body.velocity.y;

        const funnel = 0.02 + 0.15 * Math.pow(1 - dist / this.vacuumRadius, 2);
        drop.setVelocity(
          vx + (nx * 200 - vx) * funnel,
          vy + (ny * 200 - vy) * funnel,
        );
      } else {
        drop.setAcceleration(0, 0);
      }
    });
  } // ✅ CLOSE update()
} // ✅ CLOSE MainScene
